import { Badge, Button } from "@aix/ui"
import { useNavigate, useParams, useSearch } from "@tanstack/react-router"
import { useEffect, useState } from "react"
import toast from "react-hot-toast"
import { MessageSquarePlus } from "lucide-react"

import { trpc } from "../../lib/trpc"
import type { Coach } from "../../types/coach-os.types"
import OverviewTab from "./components/OverviewTab"
import WeeklyPlansTab from "./components/WeeklyPlansTab"
import PlanHistoryTab from "./components/PlanHistoryTab"
import TrackablesTab from "./components/TrackablesTab"
import ChatTab from "./components/ChatTab"
import SettingsTab from "./components/SettingsTab"
import DebugTab from "./components/DebugTab"

export default function CoachDetailScreen() {
  const navigate = useNavigate()
  const params = useParams({ strict: false }) as { coachId: string }
  const search = useSearch({ strict: false }) as { tab?: string }

  const [activeTab, setActiveTab] = useState<
    "overview" | "plans" | "history" | "trackables" | "chat" | "settings" | "debug"
  >("overview")

  // Initialize tab from URL search params
  useEffect(() => {
    const tabFromUrl = search.tab as "overview" | "plans" | "history" | "trackables" | "chat" | "settings" | "debug"
    if (
      tabFromUrl &&
      ["overview", "plans", "history", "trackables", "chat", "settings", "debug"].includes(tabFromUrl)
    ) {
      setActiveTab(tabFromUrl)
    }
  }, [search.tab])

  // Update URL when tab changes
  const handleTabChange = (newTab: "overview" | "plans" | "history" | "trackables" | "chat" | "settings" | "debug") => {
    setActiveTab(newTab)
    // @ts-ignore
    navigate({
      // @ts-ignore
      search: (prev) => ({ ...prev, tab: newTab }),
      // @ts-ignore
      replace: true,
    })
  }

  // Fetch coach data
  const {
    data: coach,
    isLoading: coachLoading,
    error: coachError,
    refetch: refetchCoach,
  } = trpc.coaches.get.useQuery({
    coachId: params.coachId,
  })

  // Start intake mutation
  const startIntakeMutation = trpc.coaches.startIntake.useMutation()

  // Back navigation
  const handleBack = () => {
    navigate({
      to: "/coach-os",
    })
  }

  // Handle start intake
  const handleStartIntake = async () => {
    try {
      const result = await startIntakeMutation.mutateAsync({
        coachId: params.coachId,
      })

      // Refetch coach to update intake status
      await refetchCoach()

      // Navigate to chat tab
      handleTabChange("chat")

      toast.success("Intake conversation started!")
    } catch (error: any) {
      toast.error(error.message || "Failed to start intake")
    }
  }

  if (coachError) {
    return (
      <div className="container mx-auto p-6">
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6">
          <h2 className="text-xl font-bold text-destructive mb-2">Error Loading Coach</h2>
          <p className="text-muted-foreground">{coachError.message}</p>
          <button
            onClick={handleBack}
            className="mt-4 px-4 py-2 bg-background border border-border rounded-lg hover:bg-secondary transition-colors"
          >
            Back to Coaches
          </button>
        </div>
      </div>
    )
  }

  if (coachLoading || !coach) {
    return (
      <div className="container mx-auto p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-20 bg-secondary rounded-lg"></div>
          <div className="h-12 bg-secondary rounded-lg w-1/2"></div>
          <div className="h-64 bg-secondary rounded-lg"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto p-3 md:p-6">
      {/* Header */}
      <div className="mb-4 md:mb-6">
        <button
          onClick={handleBack}
          className="text-muted-foreground hover:text-foreground mb-3 md:mb-4 flex items-center gap-2 text-sm md:text-base"
        >
          <span>←</span> Back to Coaches
        </button>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
          <div className="flex items-center gap-3 md:gap-4">
            <div className="text-4xl md:text-5xl">{coach.icon}</div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground">{coach.name}</h1>
              <div className="flex flex-wrap gap-1 md:gap-2 mt-2">
                <Badge variant={coach.status === "active" ? "default" : "secondary"} className="text-xs">
                  {coach.status}
                </Badge>
                <Badge variant="outline" className="text-xs">
                  {coach.type}
                </Badge>
                <Badge variant="outline" className="text-xs">
                  {coach.tone}
                </Badge>
                {coach.intakeStatus && (
                  <Badge
                    variant={
                      coach.intakeStatus === "completed"
                        ? "default"
                        : coach.intakeStatus === "in-progress"
                          ? "secondary"
                          : "outline"
                    }
                    className="text-xs"
                  >
                    Intake: {coach.intakeStatus}
                  </Badge>
                )}
              </div>
            </div>
          </div>

          {/* Start Intake Button */}
          {coach.intakeStatus === "not-started" && (
            <Button
              onClick={handleStartIntake}
              loading={startIntakeMutation.isLoading}
              className="flex items-center gap-2 w-full md:w-auto text-xs md:text-sm"
            >
              <MessageSquarePlus className="w-4 h-4" />
              Start Intake
            </Button>
          )}
        </div>

        {coach.description && <p className="text-muted-foreground text-sm md:text-base">{coach.description}</p>}
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-border mb-4 md:mb-6 overflow-x-auto">
        <nav className="-mb-px flex space-x-2 md:space-x-6">
          <button
            onClick={() => handleTabChange("overview")}
            className={`py-2 px-2 md:px-3 border-b-2 font-medium text-xs md:text-sm whitespace-nowrap flex items-center gap-1 ${
              activeTab === "overview"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
            }`}
          >
            <span>📊</span> Overview
          </button>

          <button
            onClick={() => handleTabChange("plans")}
            className={`py-2 px-2 md:px-3 border-b-2 font-medium text-xs md:text-sm whitespace-nowrap flex items-center gap-1 ${
              activeTab === "plans"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
            }`}
          >
            <span>📅</span> Plans
          </button>

          <button
            onClick={() => handleTabChange("history")}
            className={`py-2 px-2 md:px-3 border-b-2 font-medium text-xs md:text-sm whitespace-nowrap flex items-center gap-1 ${
              activeTab === "history"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
            }`}
          >
            <span>📜</span> History
          </button>

          <button
            onClick={() => handleTabChange("trackables")}
            className={`py-2 px-2 md:px-3 border-b-2 font-medium text-xs md:text-sm whitespace-nowrap flex items-center gap-1 ${
              activeTab === "trackables"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
            }`}
          >
            <span>📈</span> Trackables
          </button>

          <button
            onClick={() => handleTabChange("chat")}
            className={`py-2 px-2 md:px-3 border-b-2 font-medium text-xs md:text-sm whitespace-nowrap flex items-center gap-1 ${
              activeTab === "chat"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
            }`}
          >
            <span>💬</span> Chat
          </button>

          <button
            onClick={() => handleTabChange("settings")}
            className={`py-2 px-2 md:px-3 border-b-2 font-medium text-xs md:text-sm whitespace-nowrap flex items-center gap-1 ${
              activeTab === "settings"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
            }`}
          >
            <span>⚙️</span> Settings
          </button>

          <button
            onClick={() => handleTabChange("debug")}
            className={`py-2 px-2 md:px-3 border-b-2 font-medium text-xs md:text-sm whitespace-nowrap flex items-center gap-1 ${
              activeTab === "debug"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
            }`}
          >
            <span>🐛</span> Debug
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="min-h-[500px]">
        {activeTab === "overview" && <OverviewTab coach={coach as unknown as Coach} refetchCoach={refetchCoach} />}
        {activeTab === "plans" && <WeeklyPlansTab coachId={params.coachId} coach={coach as unknown as Coach} />}
        {activeTab === "history" && <PlanHistoryTab coach={coach as unknown as Coach} />}
        {activeTab === "trackables" && <TrackablesTab coachId={params.coachId} />}
        {activeTab === "chat" && <ChatTab coachId={params.coachId} coach={coach as unknown as Coach} />}
        {activeTab === "settings" && <SettingsTab coach={coach as unknown as Coach} refetchCoach={refetchCoach} />}
        {activeTab === "debug" && <DebugTab coach={coach as unknown as Coach} />}
      </div>
    </div>
  )
}
